/* ============================================================================
   Zodiac Collection landing page — behaviour layer
   <mes-zodiac>  the page controller: celestial dial, per-sign cascade,
                 size selection -> live variant, sticky buy bar, offer countdown.
   Vanilla custom element (mirrors assets/mes-pdp-blocks.js conventions).
   Add-to-Cart is the theme's native product-form; this script never posts to cart.
   ============================================================================ */
(function () {
  'use strict';
  if (window.customElements && window.customElements.get('mes-zodiac')) return;

  var SP = 46;          /* per-sign step on the dial */
  var R = 620;          /* arc radius for glyph rotation */
  var TOTAL = 12 * SP;
  var HALF = TOTAL / 2;

  function mod(n, m) { return ((n % m) + m) % m; }

  /* seeded RNG -> deterministic starfield (matches prototype makeStars) */
  function makeStars(n, seed, spreadY) {
    var out = [], r = seed || 1;
    var rnd = function () { r = (r * 9301 + 49297) % 233280; return r / 233280; };
    for (var i = 0; i < n; i++) {
      var sz = rnd() * 2 + 1;
      out.push({
        left: (rnd() * 100).toFixed(2),
        top: (rnd() * (spreadY || 100)).toFixed(2),
        size: sz.toFixed(2),
        opacity: (rnd() * 0.32 + 0.07).toFixed(2),
        dur: (rnd() * 4 + 2.5).toFixed(1),
        delay: (rnd() * 4).toFixed(1)
      });
    }
    return out;
  }

  function stoneDot(color) {
    return 'radial-gradient(circle at 35% 30%, #ffffff55, ' + color + ' 55%)';
  }

  class MesZodiac extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      try {
        this.signs = JSON.parse(this.querySelector('[data-zl-signs]').textContent);
        var vlist = JSON.parse(this.querySelector('[data-zl-variants]').textContent);
        this.variants = {};
        this.byVariantId = {};
        /* ⛔ ALWAYS NESTED BY CLOSURE, EVEN WHEN THERE ISN'T ONE. A product with two options
           yields closure === null for every variant, which buckets under '' — one shape, one
           lookup path, no branch that only runs on one of the two products. The alternative
           (variant OR map depending on the product) puts a type check in every read. */
        this.closures = [];
        vlist.forEach(function (v) {
          var c = v.closure || '';
          var bySize = (this.variants[v.sign] = this.variants[v.sign] || {});
          (bySize[v.size] = bySize[v.size] || {})[c] = v;
          this.byVariantId[String(v.id)] = v;
          if (this.closures.indexOf(c) === -1) { this.closures.push(c); }
        }, this);
        this.closure = this.closures[0] || '';
      } catch (e) { return; }

      /* ⛔ THE METAOBJECT WINS, FIELD BY FIELD, AND ONLY WHERE IT HAS SOMETHING TO SAY.
         A sign with no entry, or an entry with a blank field, keeps the embedded value — so a
         half-populated definition degrades one FIELD at a time rather than blanking a sign.
         `stones` is replaced wholesale rather than merged: a half-merged stone pair would pair
         one stone's name with the other's meaning, which reads as authored copy and would not
         look like a bug to anyone reviewing the page. */
      try {
        var cmsEl = this.querySelector('[data-zl-signs-cms]');
        var cms = cmsEl ? JSON.parse(cmsEl.textContent) : {};
        this.signs = this.signs.map(function (s) {
          var over = cms[s.key];
          if (!over) { return s; }
          var merged = {}, k;
          for (k in s) { if (Object.prototype.hasOwnProperty.call(s, k)) { merged[k] = s[k]; } }
          for (k in over) {
            if (!Object.prototype.hasOwnProperty.call(over, k)) { continue; }
            var val = over[k];
            if (val == null || val === '') { continue; }
            if (k === 'stones' && !(Array.isArray(val) && val.length === 2
                && val[0] && val[0].name && val[1] && val[1].name)) { continue; }
            merged[k] = val;
          }
          return merged;
        });
      } catch (e) { /* embedded data stands */ }

      this.order = this.signs.map(function (s) { return s.key; });
      this.byKey = {};
      this.signs.forEach(function (s) { this.byKey[s.key] = s; }, this);

      this.reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      var saved = '';
      try { saved = localStorage.getItem('mes_zodiac_sign') || ''; } catch (e) {}
      var def = this.getAttribute('data-default') || 'leo';

      /* Ad deep-links: ?variant=<id> (Shopify-native, used by the per-sign ad redirects)
         or ?sign=<key> pre-selects that sign and takes priority over a saved sign.
         Only the sign is taken from the URL; size stays on the default. */
      var urlSign = '';
      try {
        var qs = new URLSearchParams(location.search);
        var qv = qs.get('variant');
        var qsign = (qs.get('sign') || '').trim().toLowerCase();
        if (qv && this.byVariantId[qv]) urlSign = this.byVariantId[qv].sign;
        else if (qsign && this.byKey[qsign]) urlSign = qsign;
      } catch (e) {}

      this._fromUrl = !!urlSign;
      this.sel = urlSign || ((saved && this.byKey[saved]) ? saved : (this.byKey[def] ? def : this.order[0]));
      this.size = this.getAttribute('data-default-size') || 'M';
      this.offset = this.order.indexOf(this.sel) * SP;
      this._showA = true;
      this._interacted = false;
      this._moved = false;

      this._buildDial();
      this._buildGrid();

      /* cache hooks */
      this.$ = {};
      ['heroA', 'heroB', 'heroFlash', 'constelLine', 'constelStars', 'signName', 'meta', 'trait',
       'rashiGlyph', 'sanskrit', 'planetGlyph', 'planet', 'stoneDotA', 'stoneDotB', 'stoneLine',
       'price', 'priceCompare', 'discount', 'countdown', 'addLabel', 'variantId', 'add', 'shipDate', 'closureRow', 'closureChips',
       'storyImg', 'storyGlyph', 'storyName', 'storyText',
       'stoneAName', 'stoneAMeaning', 'stoneADot2', 'stoneBName', 'stoneBMeaning', 'stoneBDot2',
       'finalGlyph', 'finalLabel', 'bar', 'barGlyph', 'barName', 'barPrice', 'barCompare',
       'mainAddRef', 'finalRef', 'dial', 'guide', 'starsBg', 'starsFinal'
      ].forEach(function (k) { this.$[k] = this.querySelector('[data-zl="' + k + '"]'); }, this);

      this.dialBtns = Array.prototype.slice.call(this.querySelectorAll('[data-zl-dial]'));
      this.chips = Array.prototype.slice.call(this.querySelectorAll('[data-zl-chip]'));
      this.gridCards = Array.prototype.slice.call(this.querySelectorAll('[data-zl-grid]'));

      this.wrapW = (this.$.dial && this.$.dial.offsetWidth) || 390;

      this._buildStarfields();
      this._bind();
      this.renderDial();
      this.applySign(this.sel, this._fromUrl, true);
      this.selectSize(this.size, true);
      this._startCountdown();
      this._startNudge();
      this._onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('scroll', this._onScroll, { passive: true });
      cancelAnimationFrame(this._raf);
      cancelAnimationFrame(this._nudgeRaf);
      clearTimeout(this._nudgeT);
      clearInterval(this._nudgeInt);
      clearInterval(this._cdInt);
    }

    /* ---------- events ---------- */
    _bind() {
      var d = this.$.dial;
      if (d) {
        d.addEventListener('pointerdown', this.onDown.bind(this));
        d.addEventListener('pointermove', this.onMove.bind(this));
        d.addEventListener('pointerup', this.onUp.bind(this));
        d.addEventListener('pointercancel', this.onUp.bind(this));
      }
      this.dialBtns.forEach(function (b) {
        b.addEventListener('click', function () {
          if (this._moved) return;
          this._endNudge();
          this.bringToCenter(b.getAttribute('data-key'));
        }.bind(this));
      }, this);
      this.chips.forEach(function (c) {
        c.addEventListener('click', function () { this.selectSize(c.getAttribute('data-size'), false); }.bind(this));
      }, this);
      this._buildClosures();
      this.gridCards.forEach(function (g) {
        g.addEventListener('click', function () {
          this._endNudge();
          this.bringToCenter(g.getAttribute('data-key'));
          this.applySign(g.getAttribute('data-key'), true);
          window.scrollTo({ top: 0, behavior: this.reduce ? 'auto' : 'smooth' });
        }.bind(this));
      }, this);

      /* size guide */
      /* V9.28.113: the size guide opens IN PLACE under the size buttons (mes-sg2-wrist), like the product
         pages: slider + drawing, flip to the chart. Sliding picks the chip live; a chart row picks it too. */
      var sg2 = this.querySelector('mes-sg2-wrist');
      var guideBtn = this.querySelector('[data-zl="guideOpen"]');
      var guideLabel = this.querySelector('[data-zl-guidelabel]');
      var sizeRow = this.querySelector('.zl-size-row');
      if (sg2) {
        if (guideBtn) { guideBtn.addEventListener('click', function () { if (sg2.toggle) { sg2.toggle(); } }); }
        var pick = function (e) {
          var code = e.detail && e.detail.code;
          var has = this.chips.some(function (c) { return c.getAttribute('data-size') === code; });
          if (has && code !== this.size) { this.selectSize(code, false); }
        }.bind(this);
        sg2.addEventListener('mes-sg2w:size', pick);
        sg2.addEventListener('mes-sg2w:commit', pick);
        sg2.addEventListener('mes-sg2w:state', function (e) {
          var d = e.detail || {};
          if (guideLabel) { guideLabel.textContent = d.open ? 'Hide guide' : 'Size guide'; }
          if (guideBtn) { guideBtn.setAttribute('aria-expanded', d.open ? 'true' : 'false'); }
          /* on the chart face the chart is the picker, so the chips step aside (product page behaviour) */
          if (sizeRow) {
            var aside = d.open && d.face === 'back';
            if (aside) { sizeRow.style.maxHeight = sizeRow.scrollHeight + 'px'; void sizeRow.offsetHeight; }
            sizeRow.classList.toggle('is-stepped-aside', aside);
            if (!aside) { setTimeout(function () { if (!sizeRow.classList.contains('is-stepped-aside')) { sizeRow.style.maxHeight = ''; } }, 450); }
          }
        });
        if (sg2.setSize) { sg2.setSize(this.size); }
      }

      this._onResize = function () { this.wrapW = (this.$.dial && this.$.dial.offsetWidth) || 390; this.renderDial(); }.bind(this);
      this._onScroll = this._rafThrottle(this._scrollCheck.bind(this));
      window.addEventListener('resize', this._onResize);
      window.addEventListener('scroll', this._onScroll, { passive: true });
    }

    _rafThrottle(fn) {
      var ticking = false, self = this;
      return function () {
        if (ticking) return;
        ticking = true;
        self._sraf = requestAnimationFrame(function () { ticking = false; fn(); });
      };
    }

    /* ---------- dial drag ---------- */
    onDown(e) {
      this._endNudge();
      cancelAnimationFrame(this._raf);
      this._drag = { x: e.clientX, off: this.offset, id: e.pointerId, target: e.currentTarget, captured: false };
      this._moved = false;
      this.$.dial.classList.add('is-dragging');
    }
    onMove(e) {
      if (!this._drag) return;
      var dx = e.clientX - this._drag.x;
      if (Math.abs(dx) > 5) {
        this._moved = true;
        if (!this._drag.captured) { try { this._drag.target.setPointerCapture(this._drag.id); } catch (_) {} this._drag.captured = true; }
      }
      var off = this._drag.off - dx;
      this.offset = off;
      this.renderDial();
      this.applySign(this.order[this.centerIndex(off)], false);
    }
    onUp() {
      if (!this._drag) return;
      this._drag = null;
      this.$.dial.classList.remove('is-dragging');
      this.animateTo(Math.round(this.offset / SP) * SP);
      var self = this;
      setTimeout(function () { self._moved = false; }, 40);
    }

    /* ---------- build dial + grid from the embedded data (single source) ---------- */
    _buildDial() {
      var host = this.querySelector('[data-zl="dial"]');
      if (!host) return;
      var html = '';
      for (var i = 0; i < this.signs.length; i++) {
        var s = this.signs[i];
        html += '<button class="zl-dial-sign" type="button" data-zl-dial data-key="' + s.key + '" aria-label="' + s.name + '">'
              +   '<span class="zl-dial-circle"><span class="zl-dial-glyph">' + s.glyph + '︎</span></span>'
              +   '<span class="zl-dial-name">' + s.name + '</span>'
              + '</button>';
      }
      host.insertAdjacentHTML('beforeend', html);
    }
    _buildGrid() {
      var host = this.querySelector('[data-zl="gridHost"]');
      if (!host) return;
      var html = '';
      for (var i = 0; i < this.signs.length; i++) {
        var s = this.signs[i];
        html += '<button class="zl-card" type="button" data-zl-grid data-key="' + s.key + '">'
              +   '<span class="zl-card-thumb" style="background-image:url(\'' + s.product + '\')"></span>'
              +   '<span class="zl-card-foot"><span class="zl-card-meta"><span class="zl-card-name">' + s.name + '</span>'
              +   '<span class="zl-card-dates">' + s.dates + '</span></span>'
              +   '<span class="zl-card-ico" data-zl-grid-ico>' + s.glyph + '︎</span></span>'
              + '</button>';
      }
      host.innerHTML = html;
    }

    centerIndex(off) { return mod(Math.round(off / SP), 12); }

    bringToCenter(key) {
      var i = this.order.indexOf(key);
      if (i < 0) return;
      var cur = this.offset;
      var target = i * SP + Math.round((cur - i * SP) / TOTAL) * TOTAL;
      this.animateTo(target);
    }

    animateTo(target) {
      cancelAnimationFrame(this._raf);
      if (this.reduce) { this.offset = target; this.renderDial(); this.applySign(this.order[this.centerIndex(target)], true); return; }
      var start = this.offset, t0 = performance.now(), dur = 360, self = this;
      var step = function (now) {
        var p = Math.min(1, (now - t0) / dur), e = 1 - Math.pow(1 - p, 3);
        self.offset = start + (target - start) * e;
        self.renderDial();
        self.applySign(self.order[self.centerIndex(self.offset)], false);
        if (p < 1) self._raf = requestAnimationFrame(step);
        else { self.offset = target; self.renderDial(); self.applySign(self.order[self.centerIndex(target)], true); }
      };
      this._raf = requestAnimationFrame(step);
    }

    renderDial() {
      var off = this.offset;
      for (var i = 0; i < this.dialBtns.length; i++) {
        var btn = this.dialBtns[i];
        var rawX = i * SP - off;
        var xx = mod(rawX + HALF, TOTAL) - HALF;
        var d = Math.abs(xx);
        var rot = Math.atan2(xx, R) * 180 / Math.PI;
        var sc = Math.max(0.66, 1.14 - d / 400);
        var t7 = 3 * SP;
        var op = d <= t7 ? Math.min(1, 1.05 - d / 600) : Math.max(0, (1.05 - t7 / 600) - (d - t7) / 110);
        var yArc = (d * d) / 720;
        var on = d < SP / 2;
        btn.style.left = 'calc(50% + ' + xx.toFixed(1) + 'px)';
        btn.style.top = (14 + yArc).toFixed(1) + 'px';
        btn.style.transform = 'translateX(-50%) rotate(' + rot.toFixed(2) + 'deg) scale(' + sc.toFixed(3) + ')';
        btn.style.opacity = op.toFixed(2);
        btn.style.zIndex = Math.round(100 - d);
        btn.style.pointerEvents = op < 0.1 ? 'none' : 'auto';
        btn.classList.toggle('is-on', on);
      }
    }

    /* ---------- idle nudge ---------- */
    _startNudge() {
      var self = this;
      this._nudgeT = setTimeout(function () {
        self._nudge();
        self._nudgeInt = setInterval(function () {
          if (self._interacted) { clearInterval(self._nudgeInt); return; }
          self._nudge();
        }, 4000);
      }, 1500);
    }
    _endNudge() {
      this._interacted = true;
      clearTimeout(this._nudgeT);
      clearInterval(this._nudgeInt);
      cancelAnimationFrame(this._nudgeRaf);
      this._nudging = false;
    }
    _nudge() {
      if (this._interacted || this._nudging || this.reduce) return;
      this._nudging = true;
      var base = Math.round(this.offset / SP) * SP, t0 = performance.now(), dur = 1150, amp = 17, self = this;
      var step = function (now) {
        if (self._interacted) { self.offset = base; self.renderDial(); self._nudging = false; return; }
        var p = Math.min(1, (now - t0) / dur);
        var e = Math.sin(p * Math.PI * 2) * (1 - p);
        self.offset = base + amp * e;
        self.renderDial();
        if (p < 1) self._nudgeRaf = requestAnimationFrame(step);
        else { self.offset = base; self.renderDial(); self._nudging = false; }
      };
      this._nudgeRaf = requestAnimationFrame(step);
    }

    /* ---------- the cascade ---------- */
    applySign(key, persist, initial) {
      var s = this.byKey[key];
      if (!s) return;
      var changed = this.sel !== key;
      this.sel = key;

      this.style.setProperty('--zl-accent', s.accent);

      if (initial) {
        this._setHero(s.product, false);
      } else if (changed) {
        this._setHero(s.product, !this.reduce);
      }
      if (changed || initial) this._drawConstel(s.constel);

      var fe = '︎'; /* text-presentation selector: render zodiac/planet chars as glyphs, not emoji */
      this._txt('signName', s.name);
      this._txt('meta', s.dates + ' · ' + s.element + ' Sign');
      this._txt('trait', '“' + s.trait + '”');
      /* Glyph: prefer the sign's own `glyph` URL. The old path DERIVED it from the product
         image by string-replacing '-product.webp' -> '-glyph.webp', which silently breaks the
         moment a product image is not a theme asset with that exact filename — a CDN URL fails
         the replace and the FULL product photo lands in the glyph slot (19 Aug 2026: six signs
         moved to Shopify Files URLs). Derivation kept only as a fallback. */
      if (this.$.rashiGlyph) {
        var glyphSrc = s.glyphImg || (s.product || '').replace('-product.webp', '-glyph.webp');
        this.$.rashiGlyph.style.backgroundImage = "url('" + String(glyphSrc).split('?')[0] + "')";
      }
      this._txt('sanskrit', s.sanskrit);
      this._txt('planetGlyph', s.planetGlyph + fe);
      this._txt('planet', s.planet);
      this._txt('stoneLine', s.stones.map(function (x) { return x.name; }).join(' · '));
      if (this.$.stoneDotA) this.$.stoneDotA.style.background = stoneDot(s.sa);
      if (this.$.stoneDotB) this.$.stoneDotB.style.background = stoneDot(s.sb);

      if (this.$.storyImg) this.$.storyImg.style.backgroundImage = "url('" + s.model + "')";
      this._txt('storyGlyph', s.glyph + fe);
      this._txt('storyName', s.name);
      this._txt('storyText', s.story);

      var a = s.stones[0] || {}, b = s.stones[1] || {};
      this._txt('stoneAName', a.name); this._txt('stoneAMeaning', a.meaning);
      this._txt('stoneBName', b.name); this._txt('stoneBMeaning', b.meaning);
      if (this.$.stoneADot2) this.$.stoneADot2.style.background = stoneDot(s.sa);
      if (this.$.stoneBDot2) this.$.stoneBDot2.style.background = stoneDot(s.sb);

      this._txt('finalGlyph', s.glyph + fe);
      this._txt('finalLabel', 'Claim Your ' + s.name);
      /* ⛔ the base label only; updateBuy() has the LAST word because a pre-order variant
         must override it, and updateBuy() runs at the end of this method. */
      this._addBase = 'Add ' + s.name + ' · to Bag';
      this._txt('addLabel', this._addBase);
      this._txt('barGlyph', s.glyph + fe);
      this._txt('barName', s.name + ' Bracelet');

      this.gridCards.forEach(function (g) {
        var on = g.getAttribute('data-key') === key;
        g.classList.toggle('is-on', on);
        var ico = g.querySelector('[data-zl-grid-ico]');
        if (ico) ico.textContent = this.byKey[g.getAttribute('data-key')].glyph + fe;
      }, this);

      this.updateBuy();

      if (persist) { try { localStorage.setItem('mes_zodiac_sign', key); } catch (e) {} }
    }

    _txt(hook, val) { var el = this.$[hook]; if (el != null && val != null) el.textContent = val; }

    _setHero(url, animate) {
      var a = this.$.heroA, b = this.$.heroB;
      if (!a || !b) return;
      var oldFront = this._heroFront || a;
      var next = (oldFront === a) ? b : a;       /* only two layers: the incoming one always lands on top */
      next.style.backgroundImage = "url('" + url + "')";
      next.style.zIndex = '2';
      oldFront.style.zIndex = '1';
      this._heroFront = next;
      if (!animate) {
        next.classList.remove('is-in', 'is-anim');
        next.style.opacity = '1';
        oldFront.style.opacity = '0';
        return;
      }
      next.style.opacity = '';
      next.classList.remove('is-in', 'is-anim');
      void next.offsetWidth;
      next.classList.add('is-in', 'is-anim');   /* heroIn: new image scales/blurs in on top */
      if (this.$.heroFlash) { this.$.heroFlash.classList.remove('is-anim'); void this.$.heroFlash.offsetWidth; this.$.heroFlash.classList.add('is-anim'); }
      var self = this, demote = oldFront, shown = next;
      clearTimeout(this._heroT);
      this._heroT = setTimeout(function () {
        shown.classList.remove('is-in', 'is-anim');
        shown.style.opacity = '1';
        demote.style.opacity = '0';             /* old layer parks hidden as the next buffer */
        if (self.$.heroFlash) self.$.heroFlash.classList.remove('is-anim');
      }, 880);
    }

    _drawConstel(pts) {
      pts = pts || [];
      if (this.$.constelLine) {
        this.$.constelLine.setAttribute('points', pts.map(function (p) { return p[0] + ',' + p[1]; }).join(' '));
        /* restart the draw animation */
        this.$.constelLine.style.animation = 'none';
        void this.$.constelLine.offsetWidth;
        this.$.constelLine.style.animation = '';
      }
      if (this.$.constelStars) {
        var html = '';
        for (var i = 0; i < pts.length; i++) {
          var topPx = (pts[i][1] / 100 * 220).toFixed(0);
          var dur = (2.8 + (i % 4) * 0.6).toFixed(1);
          var delay = (i * 0.35).toFixed(1);
          html += '<span class="zl-constel-star" style="left:' + pts[i][0] + '%;top:' + topPx + 'px;animation:zl-twinkle ' + dur + 's ease-in-out ' + delay + 's infinite"></span>';
        }
        this.$.constelStars.innerHTML = html;
      }
    }

    /* ---------- closure ----------
       ⛔ THE ROW STAYS HIDDEN WITH A SINGLE CLOSURE. On the live two-option product `closures` is
       [''] and a "Select closure" heading over one dead chip would be worse than no row at all. */
    _buildClosures() {
      var row = this.$.closureRow, box = this.$.closureChips;
      if (!row || !box) { return; }
      var list = (this.closures || []).filter(function (c) { return c; });
      if (list.length < 2) { row.hidden = true; return; }
      var html = '';
      list.forEach(function (c) {
        html += '<button type="button" class="zl-chip' + (c === this.closure ? ' is-on' : '')
             +  '" data-zl-closure data-closure="' + String(c).replace(/"/g, '&quot;') + '">'
             +  '<span class="zl-chip-label">' + c + '</span></button>';
      }, this);
      box.innerHTML = html;
      row.hidden = false;
      this.closureChips = Array.prototype.slice.call(box.querySelectorAll('[data-zl-closure]'));
      this.closureChips.forEach(function (b) {
        b.addEventListener('click', function () {
          this.selectClosure(b.getAttribute('data-closure'));
        }.bind(this));
      }, this);
    }

    selectClosure(c) {
      this.closure = c;
      (this.closureChips || []).forEach(function (b) {
        b.classList.toggle('is-on', b.getAttribute('data-closure') === c);
      });
      /* updateBuy re-runs _applyPreorder, which is the whole reason this matters: the two
         closures have different pre-order states and the button must follow the choice. */
      this.updateBuy();
    }

    /* ---------- size + variant + price ---------- */
    selectSize(size, silent) {
      this.size = size;
      this.chips.forEach(function (c) { c.classList.toggle('is-on', c.getAttribute('data-size') === size); });
      var sg2 = this.querySelector('mes-sg2-wrist');
      if (sg2 && sg2.setSize) { sg2.setSize(size); }
      this.updateBuy();
    }

    updateBuy() {
      var bySign = this.variants[this.sel] || {};
      var byClosure = bySign[this.size] || bySign[Object.keys(bySign)[0]] || {};
      /* fall back to ANY closure for this sign+size rather than showing nothing: a sign that is
         only stocked in one closure must still price and add, it just cannot offer the choice. */
      var v = byClosure[this.closure] || byClosure[Object.keys(byClosure)[0]] || null;
      if (!v) return;
      this._txt('price', v.price);
      if (this.$.priceCompare) {
        if (v.compare) { this.$.priceCompare.textContent = v.compare; this.$.priceCompare.style.display = ''; }
        else { this.$.priceCompare.style.display = 'none'; }
      }
      if (this.$.barPrice) this.$.barPrice.textContent = v.price;
      if (this.$.barCompare) {
        if (v.compare) { this.$.barCompare.textContent = v.compare; this.$.barCompare.style.display = ''; }
        else { this.$.barCompare.style.display = 'none'; }
      }
      if (this.$.variantId) this.$.variantId.value = v.id;
      if (this.$.add) this.$.add.disabled = !v.available;
      this._applyPreorder(v);
    }

    /* ---------- pre-order ----------
       THE PRE-ORDER RULE, per VARIANT (Shrey's design, 11 Sep 2026 — A388/A389/A390). Mirrors
       assets/custom.js.liquid resolvePreorderDate verbatim, restated here because this page
       renders its own buy box and shares no code with that path — change both or neither.
         sold out (available false)         → not a pre-order; Continue selling OFF at zero is a hard stop
         fixed date in the future           → counts, with or without stock on hand (a capped launch)
         rolling days, stock at zero/below  → today + N; self-disarms when stock lands; never on untracked
         both                               → whichever date is FURTHER away
       ⛔ COMPARED AT MIDNIGHT, not "now": a date-only metafield parses as UTC midnight, so
       comparing against the current time would flip the last day off several hours early in IST. */
    _resolvePreorderDate(v) {
      if (!v || v.available === false) return null;
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      var best = null;
      if (v.preorder) {
        var fixed = new Date(v.preorder);
        if (!isNaN(fixed.getTime()) && fixed > today) best = fixed;
      }
      var days = Number(v.rolling) || 0;
      if (days > 0 && v.tracked === true && Number(v.stock) <= 0) {
        var rolling = new Date(today);
        rolling.setDate(rolling.getDate() + days);
        if (!best || rolling > best) best = rolling;
      }
      return best;
    }

    _applyPreorder(v) {
      var el = this.$.shipDate;
      var d = this._resolvePreorderDate(v);
      var on = d !== null;
      if (this.$.addLabel) {
        var pre = (window.themeVariables && window.themeVariables.strings
                   && window.themeVariables.strings.preOrderButton) || 'Pre-order';
        this._txt('addLabel', on ? pre : (this._addBase || 'Add to Bag'));
      }
      if (el) {
        if (on) {
          /* local-date fields on purpose: a UTC-midnight fixed date lands at 05:30 IST the same
             day, and the rolling date is built from local midnight, so both read correctly here */
          var dd = String(d.getDate()).padStart(2, '0');
          var mm = String(d.getMonth() + 1).padStart(2, '0');
          el.textContent = 'Will ship by ' + dd + '/' + mm + '/' + d.getFullYear();
          el.hidden = false;
        } else {
          el.textContent = '';
          el.hidden = true;
        }
      }
    }

    /* ---------- sticky bar visibility ---------- */
    _scrollCheck() {
      var ref = this.$.add;
      if (!ref || !this.$.bar) return;
      var should = ref.getBoundingClientRect().bottom < 8;
      var final = this.$.finalRef;
      if (final && should) {
        var r = final.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) should = false;
      }
      this.$.bar.classList.toggle('is-shown', should);
    }

    /* ---------- offer countdown ---------- */
    _startCountdown() {
      if (!this.$.countdown) return;
      var end;
      var fixed = this.getAttribute('data-deadline');
      if (fixed) { var t = Date.parse(fixed); if (!isNaN(t)) end = t; }
      if (!end) {
        try { end = parseInt(localStorage.getItem('mes_zodiac_offer_end') || '', 10); } catch (e) {}
        if (!end || isNaN(end) || end < Date.now()) {
          end = Date.now() + 24 * 3600 * 1000;
          try { localStorage.setItem('mes_zodiac_offer_end', String(end)); } catch (e) {}
        }
      }
      this._end = end;
      var pad = function (n) { return String(n).padStart(2, '0'); };
      var self = this;
      var tick = function () {
        var r = Math.max(0, self._end - Date.now());
        var h = Math.floor(r / 3600000), m = Math.floor((r % 3600000) / 60000), s = Math.floor((r % 60000) / 1000);
        self.$.countdown.textContent = pad(h) + ':' + pad(m) + ':' + pad(s);
      };
      tick();
      this._cdInt = setInterval(tick, 1000);
    }

    /* ---------- decorative starfields ---------- */
    _buildStarfields() {
      this._paintStars(this.$.starsBg, makeStars(60, 7, 100));
      this._paintStars(this.$.starsFinal, makeStars(40, 19, 100));
    }
    _paintStars(host, stars) {
      if (!host) return;
      var html = '';
      for (var i = 0; i < stars.length; i++) {
        var st = stars[i];
        html += '<span class="zl-star" style="left:' + st.left + '%;top:' + st.top + '%;width:' + st.size + 'px;height:' + st.size + 'px;opacity:' + st.opacity + ';animation:zl-twinkle ' + st.dur + 's ease-in-out ' + st.delay + 's infinite"></span>';
      }
      host.innerHTML = html;
    }
  }

  window.customElements.define('mes-zodiac', MesZodiac);
})();
